import { useXHR } from "@sembark-travel/xhr"
import { ICabType, SelectCabTypes } from "../../CabTypes"
import * as Validator from "yup"
import {
	AddTransportServiceDialog,
	ITransportService,
	SelectTransportServiceLocations,
	TTransportServiceLocation,
	TTransportServiceLocationPoint,
} from "../../TransportServices"
import { SelectTripDestination, TTripDestination } from "../../TripDestinations"
import { memo, useCallback, useMemo, useRef, useState } from "react"
import {
	Alert,
	Box,
	Button,
	Component,
	Container,
	DeferRender,
	Icons,
	Inline,
	joinAttributes,
	Spinner,
	Stack,
	Table,
	TableDataCell,
	TableHeaderDataCell,
	Text,
} from "@sembark-travel/ui/base"
import { TFileData, transportServicesPriceXHR } from "./store"
import { Dialog, useDialog } from "@sembark-travel/ui/dialog"
import { useNavigate } from "../../router"
import {
	arrayMutators,
	DatePickerField,
	FieldArray,
	Form,
	FormApi,
	SelectField,
	SubmissionError,
	TextInputField,
	useFieldValue,
	useForm,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui/form"
import {
	transformDataToFormValues,
	transformServiceOption,
	updatePriceSeasons,
} from "./utils"
import {
	SelectTenantCurrencyInputField,
	useFunctionalCurrencyOfTenant,
} from "../../Currencies"
import config from "../../config"
import { SelectTransportServices } from "./List"
import { SelectFolder } from "../List"
import { SheetFormSubmissionAction, TSheetFolder } from "../store"
import {
	CreateSheetFileWithInFolder,
	EditSheetFileVersionDetails,
} from "../Sheets"

export interface IDateRange {
	start_date: Date | null
	end_date: Date | null
}
export type ICabTypesPrice = {
	cab_name?: string
	cab_price?: number
}
export interface ITSPSeason {
	season_id: number
	season_name: string
	date_ranges: Array<IDateRange>
	cab_types: Array<ICabType>
}
interface ITSeasonWisePrice {
	season_id: number
	cab_type_id?: number
	price?: number
}
export interface ITSPTransportService {
	transport_service_type?: ITransportService
	seasons: Array<ITSeasonWisePrice[]>
}

export interface ITransportServicePricesFormValues {
	trip_destinations?: Array<TTripDestination>
	currency: string
	timezone_offset: number
	seasons: Array<ITSPSeason>
	transport_price_services: Array<{
		transport_service_location?: TTransportServiceLocation
		transport_service_types: Array<ITSPTransportService>
	}>
	folder?: TSheetFolder
	file_name?: string
}

export interface INewServiceCredentials {
	from?: TTransportServiceLocationPoint
	to?: TTransportServiceLocationPoint
	service: string
	distance?: number
	start_time?: Date
	duration?: number
	service_title?: string
	service_details?: string
	short_code?: string
	trip_destinations?: Array<TTripDestination>
}
export function TSPCreateNewSheetWithInFolder({
	folderDetails,
}: {
	folderDetails?: TSheetFolder
}) {
	const navigate = useNavigate()
	return (
		<CreateSheetFileWithInFolder<TFileData, ITransportServicePricesFormValues>
			service={{
				saveSheetVersionDetails: (xhr, folderId, fileName, sheetData) =>
					transportServicesPriceXHR(xhr).createFile(
						sheetData,
						Number(folderId),
						fileName
					),

				uploadSheetVersion: (xhr, versionId) =>
					transportServicesPriceXHR(xhr).uploadFile(versionId),
			}}
			onSuccess={(folderId, fileId, fileVersionId) => {
				navigate(
					"/data-drive/folders/:folderId/files/:fileId/versions/:versionId/view",
					{
						params: { folderId, fileId, versionId: fileVersionId },
					}
				)
			}}
		>
			{({ createSheetFile }) => {
				return (
					<TSPFileForm
						folderDetails={folderDetails}
						onSubmit={(values, action) => {
							if (!values.folder?.id) {
								throw Error("Folder name is required")
							}
							if (!values.file_name) {
								throw Error("File name is required")
							}
							return createSheetFile(
								String(values.folder.id),
								values.file_name,
								values,
								action
							)
						}}
						extraFields={() => (
							<Stack maxWidth={"xs"} gap="4">
								<SelectField
									select={SelectFolder}
									name="folder"
									label="Enter a folder name"
									placeholder="e.g. Rajasthan"
									creatable
									required
								/>

								<TextInputField
									name="file_name"
									required
									label={"Enter File Name"}
									placeholder="e.g. Jaipur "
								/>
							</Stack>
						)}
					/>
				)
			}}
		</CreateSheetFileWithInFolder>
	)
}

export function TSPFileEditForm({
	folderId,
	fileId,
	versionId,
}: {
	folderId: string
	fileId: string
	versionId: string
}) {
	const navigate = useNavigate()
	return (
		<EditSheetFileVersionDetails<TFileData, ITransportServicePricesFormValues>
			sheetFolderId={folderId}
			sheetId={fileId}
			sheetVersionId={versionId}
			xhrFetchBaseUrl={`/data-drive/transport-services`}
			service={{
				fetchSheetVersionDetails: (xhr, fileVersionId) =>
					transportServicesPriceXHR(xhr).getFileVersionDetails(fileVersionId),

				saveSheetVersionDetails: (xhr, folderId, fileName, sheetData) =>
					transportServicesPriceXHR(xhr).createFile(
						sheetData,
						Number(folderId),
						fileName,
						Number(fileId)
					),

				uploadSheetVersion: (xhr, versionId) =>
					transportServicesPriceXHR(xhr).uploadFile(versionId),
			}}
			transformDataToFormValues={transformDataToFormValues}
			onDraftSuccess={(folderId, fileId, fileVersionId) => {
				navigate(
					"/data-drive/folders/:folderId/files/:fileId/versions/:versionId/view",
					{
						params: { folderId, fileId, versionId: fileVersionId },
					}
				)
			}}
			onUploadSuccess={(folderId, fileId, fileVersionId) => {
				navigate(
					"/data-drive/folders/:folderId/files/:fileId/versions/:versionId/view",
					{
						params: { folderId, fileId, versionId: fileVersionId },
					}
				)
			}}
		>
			{({ sheetValues, onUpdateSheetFile }) => {
				return sheetValues ? (
					<TSPFileForm
						initialValues={sheetValues}
						onSubmit={onUpdateSheetFile}
					/>
				) : (
					<Spinner padding="4" alignCenter />
				)
			}}
		</EditSheetFileVersionDetails>
	)
}

const formValidator = Validator.object().shape({
	seasons: Validator.array().of(
		Validator.object().shape({
			date_ranges: Validator.array().of(
				Validator.object().shape({
					start_date: Validator.date()
						.typeError("Please select a valid start date")
						.required("Start date is required")
						.nullable(),
					end_date: Validator.date()
						.typeError("Please select a valid end date")
						.required("End date is required")
						.nullable()
						.min(
							Validator.ref("start_date"),
							"End date must be after start date"
						),
				})
			),
		})
	),
	currency: Validator.string().required("Please select a currency"),
	trip_destinations: Validator.array().min(
		1,
		"Please provide atleast one destinations"
	),
	transport_price_services: Validator.array().of(
		Validator.object().shape({
			transport_service_location: Validator.object()
				.nullable()
				.required("Please select Service location"),
		})
	),
})

export function TSPFileForm({
	initialValues = undefined,
	onSubmit,
	folderDetails,
	extraFields,
}: {
	initialValues?: ITransportServicePricesFormValues
	onSubmit: (
		values: ITransportServicePricesFormValues,
		action: SheetFormSubmissionAction,
		isSheetUpdated?: boolean
	) => void
	folderDetails?: TSheetFolder
	extraFields?: (props: {
		form: FormApi<
			ITransportServicePricesFormValues,
			Partial<ITransportServicePricesFormValues>
		>
	}) => React.ReactNode
}) {
	const functionalCurrency = useFunctionalCurrencyOfTenant()
	const initialServiceValues: ITransportServicePricesFormValues = {
		folder: folderDetails,
		timezone_offset: config.timezoneOffset,
		currency: functionalCurrency.currency,
		trip_destinations: [],
		seasons: [
			{
				season_id: 0,
				season_name: "Season 1",
				date_ranges: [{ start_date: null, end_date: null }],
				cab_types: [],
			},
		],
		transport_price_services: [
			{
				transport_service_location: undefined,
				transport_service_types: [
					{
						transport_service_type: undefined,
						seasons: [],
					},
				],
			},
		],
	}

	const [initialFileValues] = useState<ITransportServicePricesFormValues>({
		...initialServiceValues,
		...initialValues,
		currency: initialValues?.currency || functionalCurrency.currency,
	})

	const formActionRef = useRef<SheetFormSubmissionAction>(
		SheetFormSubmissionAction.SAVE_DRAFT
	)

	return (
		<Form<ITransportServicePricesFormValues>
			initialValues={initialFileValues}
			validate={validateFormValues(formValidator)}
			onSubmit={withServerErrors(async (values, form) => {
				if (!values.seasons?.every((s) => s.cab_types?.length)) {
					throw Error("Sessions cab is required")
				}
				await onSubmit?.(values, formActionRef.current, form.getState().dirty)
			})}
			mutators={{ ...arrayMutators }}
			subscription={{ submitError: true }}
		>
			{({ handleSubmit, form, submitting }) => {
				return (
					<form onSubmit={handleSubmit}>
						<Container paddingY="8" bgColor="default">
							<Stack gap="4">
								{extraFields ? <>{extraFields({ form })}</> : null}

								<SelectField
									select={SelectTripDestination}
									name="trip_destinations"
									label="Trip Destinations"
									multiple
									fetchOnMount
									closeOnSelect
									onChange={(
										destinations: Array<TTripDestination> | undefined
									) => {
										form.change("trip_destinations", destinations)
										if (destinations?.length) {
											form.change("currency", destinations[0].currency)
										}
									}}
								/>
								<Inline gap="4" collapseBelow="sm">
									<SelectTenantCurrencyInputField
										name="currency"
										label="Currency"
										style={{ maxWidth: "150px" }}
									/>
								</Inline>
							</Stack>
						</Container>
						<hr />
						<DeferRender by={100}>
							<Table
								aria-label="CSV file format for transport service prices"
								responsive
								className="excel-style-table price-table"
								style={{
									width: "100%",
									maxHeight: "100vh",
									overflowY: "auto",
								}}
								paddingBottom={"16"}
							>
								<thead
									style={{
										position: "sticky",
										top: 0,
										zIndex: 0,
										borderTop: "1px solid #ccc",
									}}
								>
									<tr>
										<TableHeaderDataCell
											rowSpan={3}
											bgColor="default"
											style={{
												position: "sticky",
												left: 0,
												minWidth: "200px",
												zIndex: 0,
											}}
										>
											Transport Service Location
										</TableHeaderDataCell>
										<TableHeaderDataCell
											rowSpan={3}
											bgColor="default"
											style={{
												position: "sticky",
												minWidth: "400px",
												left: "400px",
												zIndex: 0,
											}}
										>
											Transport Service Type
										</TableHeaderDataCell>

										{/* Season Header */}
										<FieldArray<ITSPSeason> name={`seasons`} subscription={{}}>
											{({ fields }) => {
												return fields.map((name, index) => (
													<SeasonHeader
														key={index}
														name={name}
														index={index}
														fieldLength={fields.length || 0}
														onRemoveFromIndex={fields.remove}
													/>
												))
											}}
										</FieldArray>
									</tr>
									{/* Season Date Range */}
									<tr>
										<FieldArray<ITSPSeason> name={`seasons`}>
											{({ fields }) =>
												fields.map((name, index) => (
													<SeasonDateRanges key={index} name={name} />
												))
											}
										</FieldArray>
									</tr>
									{/* Season Cab Types */}
									<tr>
										<SeasonCabTypes />
									</tr>
								</thead>
								<tbody>
									<FieldArray name="transport_price_services" subscription={{}}>
										{({ fields }) => {
											return fields.map((name, locationIndex) => (
												// <DeferRender
												// 	key={locationIndex}
												// 	by={10 * locationIndex}
												// 	noLoader
												// >
												<TransportServiceLocation
													key={locationIndex}
													name={name}
													locationIndex={locationIndex}
													fieldLength={fields.length || 0}
													onRemoveFromIndex={fields.remove}
													onAddingValues={fields.push}
												/>
												// </DeferRender>
											))
										}}
									</FieldArray>
								</tbody>
							</Table>
							<Stack paddingY="4" gap="2">
								<SubmissionError />
								<Inline gap="2">
									<Button
										type="submit"
										level="primary"
										disabled={submitting}
										onClick={() =>
											(formActionRef.current = SheetFormSubmissionAction.UPLOAD)
										}
									>
										{submitting &&
										formActionRef.current === SheetFormSubmissionAction.UPLOAD
											? "Uploading..."
											: "Upload Prices"}
									</Button>
									<Button
										type="submit"
										level="secondary"
										disabled={submitting}
										onClick={() =>
											(formActionRef.current =
												SheetFormSubmissionAction.SAVE_DRAFT)
										}
									>
										{submitting &&
										formActionRef.current ===
											SheetFormSubmissionAction.SAVE_DRAFT
											? "Saving..."
											: "Save Draft"}
									</Button>
								</Inline>
							</Stack>
						</DeferRender>
					</form>
				)
			}}
		</Form>
	)
}

const TransportServiceLocation = memo(
	({
		fieldLength,
		name,
		locationIndex,
		onRemoveFromIndex,
		onAddingValues,
	}: {
		fieldLength: number
		name: string
		locationIndex: number
		onRemoveFromIndex?: (index: number) => void
		onAddingValues?: (
			data: ITransportServicePricesFormValues["transport_price_services"][number]
		) => void
	}) => {
		const xhr = useXHR()
		const form = useForm()
		const [isDeleteDialogOpen, openDeleteDialog, closeDeleteDialog] =
			useDialog()
		const { value: serviceLocation } = useFieldValue<
			| undefined
			| ITransportServicePricesFormValues["transport_price_services"][number]["transport_service_location"]
		>(`${name}.transport_service_location`)
		const { value: transport_service_types } = useFieldValue<
			| undefined
			| ITransportServicePricesFormValues["transport_price_services"][number]["transport_service_types"]
		>(`${name}.transport_service_types`)

		const zIndex = useMemo(() => -(locationIndex + 2), [locationIndex])
		const rowSpan = useMemo(
			() =>
				transport_service_types?.length
					? transport_service_types.length + 1
					: 2,
			[transport_service_types?.length]
		)
		const initialValue = useMemo(() => {
			return {
				from: serviceLocation?.from,
				to: serviceLocation?.to,
				short_code: serviceLocation?.short_code,
				service: "",
			}
		}, [serviceLocation])

		const onRemove = useCallback(() => {
			onRemoveFromIndex?.(locationIndex)
		}, [locationIndex, onRemoveFromIndex])

		const onAdding = useCallback(() => {
			onAddingValues?.({
				transport_service_location: undefined,
				transport_service_types: [
					{
						transport_service_type: undefined,
						seasons: transport_service_types?.[0]?.seasons || [],
					},
				],
			})
		}, [onAddingValues, transport_service_types])

		return (
			<Component
				initialState={
					serviceLocation?.services?.map(transformServiceOption) || []
				}
				onMount={({ state, setState }) => {
					if (serviceLocation && !state?.length) {
						xhr
							.get(`/transport-service-locations/${serviceLocation.id}`)
							.then((resp) => resp.data.data)
							.then((location) => {
								setState(location.services?.map(transformServiceOption))
							})
					}
				}}
			>
				{({ state: serviceOptions, setState }) => (
					<>
						{/* Transport Service Location */}
						<tr>
							<TableDataCell
								rowSpan={rowSpan}
								bgColor="default"
								style={{
									position: "sticky",
									minWidth: "400px",
									left: 0,
									zIndex: zIndex,
								}}
							>
								<Inline gap={"4"} alignItems="start">
									<SelectField
										style={{
											position: "relative",
										}}
										select={SelectTransportServiceLocations}
										name={`${name}.transport_service_location`}
										creatable
										multiple={false}
										help={
											serviceLocation?.from ? (
												<Text>{serviceLocation?.name}</Text>
											) : null
										}
										onChange={(value: TTransportServiceLocation) => {
											form.batch(() => {
												form.change(
													`${name}.transport_service_location` as never,
													value as never
												)
												if (value?.services?.length) {
													const options = value.services.map(
														transformServiceOption
													)
													setState(options)
													const seasons = transport_service_types?.[0]?.seasons

													const newTransportServiceTypes = options.map(
														(option) => ({
															transport_service_type: option,
															seasons: seasons, // Copy seasons as is
														})
													)

													// Update the field in the form
													form.change(
														`${name}.transport_service_types` as never,
														newTransportServiceTypes as never
													)
												} else {
													form.change(
														`${name}.transport_service_types` as never,
														// undefined as never
														transport_service_types?.map((type, index) => ({
															...type,
															transport_service_type: undefined, // Update for each index
														})) as never
													)
												}
											})
										}}
									/>
									<Inline paddingTop={"2"} gap={"1"}>
										{fieldLength === locationIndex + 1 && (
											<Button inline type="button" onClick={onAdding}>
												<Icons.Plus />
											</Button>
										)}
										{fieldLength > 1 && (
											<>
												<Button inline onClick={openDeleteDialog}>
													<Icons.Trash />
												</Button>
												<WarningDialogBox
													open={isDeleteDialogOpen}
													onClose={closeDeleteDialog}
													onSuccess={onRemove}
													message={serviceLocation?.name || "Transport service"}
												/>
											</>
										)}
									</Inline>
								</Inline>
							</TableDataCell>
						</tr>

						{/* location transport service types  */}
						<FieldArray name={`${name}.transport_service_types`}>
							{({ fields }) =>
								fields.map((name, index) => {
									const typeZIndex = -(index + 1 + locationIndex * 20)
									return (
										<tr key={index}>
											<TableDataCell
												bgColor="default"
												style={{
													position: "sticky",
													minWidth: "400px",
													left: "400px",
													zIndex: typeZIndex,
												}}
											>
												<TransportServiceType
													fieldLength={fields.length || 0}
													name={name}
													index={index}
													serviceOptions={serviceOptions}
													initialValues={initialValue}
													onRemoveFromIndex={fields.remove}
													onDuplicateUsingValues={fields.push}
													onAddTransportServiceType={(service) => {
														fields.push({
															transport_service_type:
																transformServiceOption(service),
															seasons: transport_service_types?.[0]?.seasons,
														})
													}}
												/>
											</TableDataCell>
											<SeasonPrices name={name} typeZIndex={typeZIndex} />
										</tr>
									)
								})
							}
						</FieldArray>
					</>
				)}
			</Component>
		)
	}
)

const TransportServiceType = memo(
	({
		fieldLength,
		name,
		index,
		serviceOptions,
		initialValues,
		onRemoveFromIndex,
		onDuplicateUsingValues,
		onAddTransportServiceType,
	}: {
		fieldLength: number
		name: string
		index: number
		serviceOptions: Array<ITransportService>
		initialValues: INewServiceCredentials
		onRemoveFromIndex?: (index: number) => void
		onDuplicateUsingValues?: (service: ITransportService | undefined) => void
		onAddTransportServiceType?: (service: ITransportService) => void
	}) => {
		const { value: transportDestination } =
			useFieldValue<ITransportServicePricesFormValues["trip_destinations"]>(
				"trip_destinations"
			)
		const { value: service } =
			useFieldValue<
				ITransportServicePricesFormValues["transport_price_services"][number]["transport_service_types"][number]["transport_service_type"]
			>(name)

		const removeTransportServiceType = useCallback(() => {
			onRemoveFromIndex?.(index)
		}, [onRemoveFromIndex, index])

		const duplicateTransportServiceType = useCallback(() => {
			onDuplicateUsingValues?.(service)
		}, [onDuplicateUsingValues, service])

		const addTransportServiceType = useCallback(
			(value: ITransportService) => {
				onAddTransportServiceType?.(value)
			},
			[onAddTransportServiceType]
		)

		return (
			<Stack gap={"4"}>
				<Inline gap={"4"}>
					<SelectField
						select={SelectTransportServices}
						name={`${name}.transport_service_type`}
						multiple={false}
						options={serviceOptions}
						tripDestinations={transportDestination}
						initialValues={initialValues}
						labelKey="label"
						creatable
						fullWidth
						help={({ value }: { value: unknown }) => {
							const serviceValue = value as ITransportService | undefined
							return serviceValue?.deleted_at ? (
								<Alert status="warning" inline>
									Service disabled. Please select a different one.
								</Alert>
							) : serviceValue?.start_time || serviceValue?.duration ? (
								<>
									<Text>{serviceValue?.name}</Text>
									<Text>
										{joinAttributes(
											serviceValue?.distance
												? `${serviceValue?.distance} kms`
												: null,
											serviceValue?.start_time_formatted
												? `Starts: ${serviceValue?.start_time_formatted}`
												: null,
											serviceValue?.duration_string || null,
											serviceValue?.end_time_formatted
												? `Ends: ${serviceValue?.end_time_formatted}`
												: null
										)}
									</Text>
								</>
							) : (
								<Text>{serviceValue?.name}</Text>
							)
						}}
					/>

					{fieldLength === index + 1 && (
						<>
							<AddTransportServices
								tripDestinations={transportDestination}
								initialValues={initialValues}
								onSuccess={(values: ITransportService) => {
									addTransportServiceType(values)
								}}
							>
								{({ create }) => (
									<Button onClick={create}>
										<Icons.Plus />
									</Button>
								)}
							</AddTransportServices>
							<Button
								type="button"
								onClick={() => {
									duplicateTransportServiceType()
								}}
							>
								<Icons.Duplicate />
							</Button>
						</>
					)}
					{(fieldLength || 0) > 1 && (
						<>
							<Button
								type="button"
								onClick={() => {
									removeTransportServiceType()
								}}
							>
								<Icons.MinusCircle />
							</Button>
						</>
					)}
				</Inline>
			</Stack>
		)
	}
)
const seasonValidation = validateFormValues(
	Validator.object()
		.required()
		.shape({
			season_name: Validator.string()
				.required("Please provide a season name")
				.min(3, "Please atleast 3 characters")
				.max(20, "Please use less then 20 characters"),
			cab_types: Validator.array().min(1, "Please provide atleast one cab"),
			date_ranges: Validator.array()
				.min(1, "Please provide atleast one date interval")
				.of(
					Validator.object().shape({
						start_date: Validator.date()
							.typeError("Please select a valid start date")
							.required("Start date is required")
							.nullable(),
						end_date: Validator.date()
							.typeError("Please select a valid end date")
							.required("End date is required")
							.nullable()
							.min(
								Validator.ref("start_date"),
								"End date must be after start date"
							),
					})
				),
		})
)
const SeasonDialogBox = memo(
	({
		open,
		onClose,
		onSuccess,
		initialValues,
	}: {
		open: boolean
		onClose: () => void
		initialValues: ITSPSeason
		onSuccess?: (season: ITSPSeason) => void
	}) => {
		return (
			<Dialog open={open} onClose={onClose} title="Make Season" sm>
				<Form<ITSPSeason>
					initialValues={initialValues}
					validate={seasonValidation}
					onSubmit={(seasonData) => {
						onSuccess?.(seasonData)
						onClose()
					}}
					mutators={{
						...arrayMutators,
					}}
				>
					{({ handleSubmit }) => (
						<form onSubmit={handleSubmit}>
							<Dialog.Body>
								<Stack gap="4">
									<TextInputField
										label="Season Name"
										name={`season_name`}
										type="text"
										required
									/>
									{/* date range */}
									<FieldArray<IDateRange>
										name={`date_ranges`}
										subscription={{}}
									>
										{({ fields: dateRangeFields }) => (
											<>
												{dateRangeFields.map(
													(dateRangeName, dateRangeIndex) => (
														<Inline
															gap="4"
															alignItems={"start"}
															key={dateRangeIndex}
														>
															<DatePickerField
																label="Start Date"
																name={`${dateRangeName}.start_date`}
																required
															/>
															<DatePickerField
																label="End Date"
																name={`${dateRangeName}.end_date`}
																required
															/>
															<Box paddingTop={"6"}>
																<Button
																	type="button"
																	onClick={() =>
																		dateRangeFields.push({
																			start_date: null,
																			end_date: null,
																		})
																	}
																>
																	<Icons.Duplicate />
																</Button>

																{(dateRangeFields.length || 0) > 1 && (
																	<Button
																		type="button"
																		onClick={() =>
																			dateRangeFields.remove(dateRangeIndex)
																		}
																	>
																		<Icons.Trash />
																	</Button>
																)}
															</Box>
														</Inline>
													)
												)}
											</>
										)}
									</FieldArray>
									<SelectField
										select={SelectCabTypes}
										name={`cab_types`}
										label="Cab Type"
										multiple
										fetchOnMount
										creatable
									/>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Button type="submit">Submit</Button>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		)
	}
)

function WarningDialogBox({
	open,
	onClose,
	onSuccess,
	message,
}: {
	open: boolean
	onClose: () => void
	onSuccess: () => void
	message: string
}) {
	return (
		<Dialog open={open} onClose={onClose} title="Warning" sm>
			<Dialog.Body>
				<Stack gap="4">
					<Alert status="warning">
						Are you sure you want to delete <b>{message}</b>
					</Alert>
				</Stack>
			</Dialog.Body>
			<Dialog.Footer>
				<Button onClick={onClose}>Cancel</Button>
				<Button
					type="submit"
					onClick={() => {
						onSuccess()
						onClose()
					}}
				>
					Delete
				</Button>
			</Dialog.Footer>
		</Dialog>
	)
}

function AddTransportServices({
	children,
	onSuccess,
	initialValues,
	tripDestinations,
}: {
	children: (props: { create: () => void }) => React.ReactNode
	onSuccess: (value: ITransportService) => void
	tripDestinations?: Array<TTripDestination>
	initialValues?: INewServiceCredentials
}) {
	const [state, setState] = useState(false)
	const promiseOfCreation = useRef<
		((transportService: ITransportService) => void) | undefined
	>(undefined)
	return (
		<>
			{children({ create: () => setState(true) })}
			{state ? (
				<AddTransportServiceDialog
					open={state}
					onClose={() => {
						setState(false)
					}}
					initialValues={{
						...initialValues,
						trip_destinations: tripDestinations,
					}}
					onSuccess={(transportService) => {
						promiseOfCreation.current?.(transportService)
						onSuccess(transportService)
					}}
					type="addLocationService"
					serviceLocationId={undefined}
					canAddMoreServices={false}
					url={`/transport-services`}
				/>
			) : null}
		</>
	)
}

const CabTypePrices = memo(
	({
		tname,
		name,
		typeZIndex,
	}: {
		tname: string
		name: string
		typeZIndex: number
	}) => {
		return (
			<FieldArray name={`${name}.cab_types`}>
				{({ fields }) =>
					fields.map((fieldName, index) => {
						return (
							<TableDataCell
								key={index}
								style={{
									position: "relative",
									zIndex: typeZIndex - 2,
								}}
							>
								<TextInputField
									name={`${tname}.${name}.${index}.price`}
									type="number"
									min={0}
									step="0.01"
									placeholder="e.g. 20"
								/>
							</TableDataCell>
						)
					})
				}
			</FieldArray>
		)
	}
)
const SeasonPrices = memo(
	({ name, typeZIndex }: { name: string; typeZIndex: number }) => {
		return (
			<FieldArray name={`seasons`}>
				{({ fields }) =>
					fields.map((seasonName, index) => {
						return (
							<CabTypePrices
								key={index}
								tname={name}
								name={seasonName}
								typeZIndex={typeZIndex}
							/>
						)
					})
				}
			</FieldArray>
		)
	}
)

const SeasonHeader = memo(
	({
		name,
		index,
		fieldLength,
		onRemoveFromIndex,
	}: {
		name: string
		index: number
		fieldLength: number
		onRemoveFromIndex?: (index: number) => void
	}) => {
		const [isSeasonDialogOpen, openSeasonDialog, closeSeasonDialog] =
			useDialog()
		const [isDeleteDialogOpen, openDeleteDialog, closeDeleteDialog] =
			useDialog()
		const form = useForm()
		const { value: seasonValue } = useFieldValue<ITSPSeason>(name)
		const handleSeasonUpdate = useCallback(
			(newSeasonValue: ITSPSeason) => {
				const updatedServices = {
					season_id: index,
					season_name: newSeasonValue.season_name,
					date_ranges: newSeasonValue.date_ranges,
					cab_types: newSeasonValue.cab_types,
				}

				form.change(name, updatedServices)
				const currentValues: ITransportServicePricesFormValues["transport_price_services"] =
					form.getState().values.transport_price_services
				const updatedSeasons: ITSPSeason[] = form.getState().values.seasons
				const oldCabs = new Set(seasonValue.cab_types.map((cab) => cab.id))
				const newCabs = new Set(updatedServices.cab_types.map((cab) => cab.id))

				const cabDiff = [
					...[...oldCabs].filter((cab) => !newCabs.has(cab)), // Removed cabs
					...[...newCabs].filter((cab) => !oldCabs.has(cab)), // Added cabs
				]

				if (cabDiff.length > 0) {
					const newPrice = updatePriceSeasons({
						currentValues,
						seasonValue: updatedSeasons,
					})
					form.change("transport_price_services", newPrice)
				}
			},
			[form, index, name, seasonValue.cab_types]
		)

		const handleDuplicate = useCallback(() => {
			const currentValues: ITransportServicePricesFormValues["transport_price_services"] =
				form.getState().values.transport_price_services
			const allServices: ITSPSeason[] = form.getState().values.seasons

			const updatedServices = {
				season_id: index + 1,
				season_name: allServices[index].season_name + " (Copy)",
				date_ranges: allServices[index].date_ranges,
				cab_types: allServices[index].cab_types,
			}

			form.change(`seasons[${index + 1}]`, updatedServices)

			const updatedSeason: ITSPSeason[] = form.getState().values.seasons
			const newPrice = updatePriceSeasons({
				currentValues,
				seasonValue: updatedSeason,
			})
			form.change("transport_price_services", newPrice)
		}, [form, index])

		const handleDelete = useCallback(() => {
			const currentValues = form.getState().values
			const updatedServices = currentValues.transport_price_services.map(
				(
					service: ITransportServicePricesFormValues["transport_price_services"][number]
				) => ({
					...service,
					transport_service_types: service.transport_service_types.map(
						(
							type: ITransportServicePricesFormValues["transport_price_services"][number]["transport_service_types"][number]
						) => ({
							...type,
							seasons: type.seasons.filter((_, i) => i !== index),
						})
					),
				})
			)
			onRemoveFromIndex?.(index)
			form.change("transport_price_services", updatedServices)
		}, [form, index, onRemoveFromIndex])

		return (
			<TableHeaderDataCell
				bgColor={"default"}
				colSpan={seasonValue?.cab_types?.length || 1}
			>
				<Inline gap="1" alignItems="center" justifyContent="between">
					<Inline gap="1" alignItems="center">
						<Text>{seasonValue?.season_name || "Season Name"}</Text>
						<Button onClick={openSeasonDialog}>
							<Icons.Pencil />
						</Button>
						<SeasonDialogBox
							open={isSeasonDialogOpen}
							onClose={closeSeasonDialog}
							initialValues={seasonValue}
							onSuccess={handleSeasonUpdate}
						/>
					</Inline>

					<Inline gap="1" alignItems="center">
						{fieldLength === index + 1 && (
							<Button type="button" onClick={handleDuplicate}>
								<Icons.Plus />
							</Button>
						)}

						{fieldLength > 1 && (
							<>
								<Button onClick={openDeleteDialog}>
									<Icons.Trash />
								</Button>
								<WarningDialogBox
									open={isDeleteDialogOpen}
									onClose={closeDeleteDialog}
									onSuccess={handleDelete}
									message={seasonValue?.season_name || "Season Name"}
								/>
							</>
						)}
					</Inline>
				</Inline>
			</TableHeaderDataCell>
		)
	}
)

const SeasonCabTypes = memo(() => {
	const { value: seasons } = useFieldValue<ITSPSeason[]>("seasons")

	return seasons.map((season) =>
		season.cab_types.map((cab, index) => (
			<TableDataCell
				key={index}
				bgColor={"default"}
				style={{
					maxWidth: "10px",
					position: "relative",
					zIndex: -2,
				}}
			>
				<Text>{cab.name}</Text>
			</TableDataCell>
		))
	)
})

const SeasonDateRanges = memo(({ name }: { name: string }) => {
	const { value: cabs } = useFieldValue<ITSPSeason["cab_types"]>(
		`${name}.cab_types`
	)

	return (
		<TableDataCell
			colSpan={cabs.length}
			bgColor={"default"}
			style={{ position: "relative", zIndex: -1 }}
		>
			<Stack gap={"2"}>
				<FieldArray<IDateRange> name={`${name}.date_ranges`}>
					{({ fields: dateRangeFields }) =>
						dateRangeFields.map((dateRangeName, dateRangeIndex) => (
							<Inline gap="4" alignItems={"center"} key={dateRangeIndex}>
								<DatePickerField
									name={`${dateRangeName}.start_date`}
									required
								/>
								<DatePickerField
									name={`${dateRangeName}.end_date`}
									rightAlign={true}
									required
								/>
								<Button
									type="button"
									onClick={() =>
										dateRangeFields.push({
											start_date: null,
											end_date: null,
										})
									}
								>
									<Icons.Duplicate />
								</Button>

								{(dateRangeFields.length || 0) > 1 && (
									<Button
										type="button"
										onClick={() => dateRangeFields.remove(dateRangeIndex)}
									>
										<Icons.Trash />
									</Button>
								)}
							</Inline>
						))
					}
				</FieldArray>
			</Stack>
		</TableDataCell>
	)
})
